// Main app shell: sidebar (category filter + lock), list (search + entries), detail.

import { el, clear } from './dom.js';
import { countsByType, mountList, filterEntries, nextIndex } from './list.js';
import { renderDetail, teardownDetail } from './detail.js';
import { openEntryForm } from './form.js';
import { openPasswordGenerator } from './password-generator.js';
import { openImportDialog } from './import.js';
import { openCsvImportDialog } from './csv-import.js';
import { openMenu } from './menu.js';
import { entriesToCsv } from '../export/csv-codec.js';
import { renderSyncStatus } from './sync-status.js';
import { openConflictDialog, openFirstSyncDialog } from './sync-modals.js';
import { SyncConflictError } from '../sync/puter-sync.js';
import { renderTopbar } from './topbar.js';
import { renderTabbar } from './tabbar.js';
import { fetchFavicon } from '../util/favicon.js';
import { applyAttachmentOps } from '../storage/attachment-ops.js';

const CATEGORIES = [
  { key: 'all', label: 'All Items' },
  { key: 'starred', label: 'Starred' },
  { key: 'login', label: 'Logins' },
  { key: 'card', label: 'Cards' },
  { key: 'license', label: 'Licenses' },
  { key: 'identity', label: 'Identities' },
  { key: 'note', label: 'Notes' },
  { key: 'apiKey', label: 'API Keys' },
];

export function renderShell(container, { vault, autoLock, sync, onLocked }) {
  clear(container);

  const state = {
    entries: [],
    search: '',
    filter: 'all',
    selectedId: null,
    syncStatus: 'idle',
  };

  state.mobile = { view: 'list' };

  function pushDetailHistory(id) {
    try { history.pushState({ __detail: id }, '', location.href); } catch { /* ignore */ }
  }

  function onPopState(e) {
    // Only exit detail view if the new history state is neither our detail
    // marker nor a sheet marker. Sheet popstate is handled by sheet.js;
    // when popping a sheet entry we should leave the detail view intact.
    if (state.mobile.view === 'detail'
        && (!e.state || (!e.state.__detail && !e.state.__sheet))) {
      state.selectedId = null;
      setView('list');
      renderList();
      renderDetailPane();
    }
  }
  window.addEventListener('popstate', onPopState);

  const sidebar = el('aside', { class: 'sidebar' });
  const list = el('section', { class: 'list' });
  const detail = el('section', { class: 'detail' });
  const topbar = el('header', { class: 'topbar' });
  const tabbar = el('nav', { class: 'tabbar' });
  const shell = el('div', { class: 'shell' }, [topbar, sidebar, list, detail, tabbar]);
  shell.dataset.view = 'list';
  container.append(shell);

  function setView(view) {
    state.mobile.view = view;
    shell.dataset.view = view;
    renderTopbarMobile();
  }

  const listApi = mountList(list, {
    initialSearch: state.search,
    onSearchChange: (v) => { state.search = v; renderList(); },
    onNew: () => openForm(null),
  });

  async function refresh() {
    state.entries = await vault.getAll();
    if (state.selectedId && !state.entries.find((e) => e.id === state.selectedId)) {
      state.selectedId = null;
    }
    render();
  }

  function render() {
    renderSidebar();
    renderList();
    renderDetailPane();
    renderTopbarMobile();
    renderTabbarMobile();
  }

  function renderList() {
    listApi.update({
      entries: state.entries,
      search: state.search,
      filter: state.filter,
      selectedId: state.selectedId,
      onSelect: (id) => {
        state.selectedId = id;
        setView('detail');
        pushDetailHistory(id);
        renderList();
        renderDetailPane();
        renderTopbarMobile();
      },
    });
  }

  function renderDetailPane() {
    const selected = state.entries.find((e) => e.id === state.selectedId) ?? null;
    renderDetail(detail, {
      entry: selected,
      vault,
      onEdit: () => selected && openForm(selected),
      onToggleStar: async () => {
        if (!selected) return;
        await vault.upsert({ ...selected, starred: !selected.starred });
        await refresh();
      },
      onDelete: async () => {
        if (!selected) return;
        await vault.remove(selected.id);
        state.selectedId = null;
        setView('list');
        await refresh();
        try { history.back(); } catch { /* ignore */ }
      },
      onBack: () => {
        state.selectedId = null;
        setView('list');
        renderList();
        renderDetailPane();
        renderTopbarMobile();
        try { history.back(); } catch { /* ignore */ }
      },
      onMutated: async () => {
        await refresh();
        renderDetailPane();
      },
    });
  }

  function renderSidebar() {
    clear(sidebar);
    const counts = countsByType(state.entries);
    const nav = el('nav', {});
    for (const cat of CATEGORIES) {
      const n = counts[cat.key] ?? 0;
      nav.append(el('button', {
        class: state.filter === cat.key ? 'active' : '',
        onclick: () => { state.filter = cat.key; state.selectedId = null; render(); },
      }, [el('span', {}, [cat.label]), el('span', { class: 'count' }, [String(n)])]));
    }

    const syncSlot = el('div');
    if (sync) renderSyncStatus(syncSlot, { status: state.syncStatus, onClick: () => triggerSync() });

    const menuBtn = el('button', { title: 'More actions', 'aria-label': 'More actions' }, ['⋯']);
    menuBtn.addEventListener('click', () => {
      openMenu(menuBtn, [
        { label: 'Import from 1Password (.1pif)…', onClick: () => openImportDialog({ vault, autoLock, onDone: refresh }) },
        { label: 'Import from CSV…', onClick: () => openCsvImportDialog({ vault, autoLock, onDone: refresh }) },
        { separator: true },
        { label: 'Export encrypted backup (.enc)', onClick: async () => {
          const bytes = await vault.export();
          downloadBytes(bytes, vaultFilename('.enc'), 'application/octet-stream');
        } },
        { label: 'Export as CSV (plaintext)', onClick: async () => {
          const all = await vault.getAll();
          const { csv, skippedAttachments } = entriesToCsv(all);
          if (skippedAttachments > 0 &&
              !confirm(`Export will include ${all.length} entries. ${skippedAttachments} attachment file(s) cannot be represented in CSV and will not be exported. Continue?`)) return;
          downloadBytes(new TextEncoder().encode(csv), vaultFilename('.csv'), 'text/csv;charset=utf-8');
        } },
        { separator: true },
        { label: 'Sync now', onClick: () => triggerSync(), disabled: !sync },
        { label: 'Open recovery tool', onClick: () => {
          window.open('recover.html', '_blank', 'noopener');
        } },
      ]);
    });

    const footer = el('div', { class: 'footer' }, [
      menuBtn,
      el('button', { class: 'danger', onclick: () => {
        try { history.replaceState({}, '', location.pathname); } catch { /* ignore */ }
        vault.lock();
        onLocked();
      }, title: 'Lock (⌘L)' }, ['Lock']),
    ]);

    const genBtn = el('button', {
      class: 'sidebar-action',
      onclick: () => openPasswordGenerator({}),
    }, ['Generate password']);

    sidebar.append(el('div', { class: 'brand' }, ['Vault']), nav, genBtn, syncSlot, footer);
  }

  function buildMenuItems() {
    // Mobile ⋯ menu. Overlaps the desktop sidebar ⋯ menu but is maintained
    // separately; mobile also surfaces Generate here since the sidebar is hidden.
    return [
      { label: 'Generate password…', onClick: () => openPasswordGenerator({}) },
      { separator: true },
      { label: 'Sync now', onClick: () => triggerSync(), disabled: !sync },
      { separator: true },
      { label: 'Import from 1Password (.1pif)…', onClick: () => openImportDialog({ vault, autoLock, onDone: refresh }) },
      { label: 'Import from CSV…', onClick: () => openCsvImportDialog({ vault, autoLock, onDone: refresh }) },
      { separator: true },
      { label: 'Export encrypted backup (.enc)', onClick: async () => {
        const bytes = await vault.export();
        downloadBytes(bytes, vaultFilename('.enc'), 'application/octet-stream');
      } },
      { label: 'Export as CSV (plaintext)', onClick: async () => {
        const all = await vault.getAll();
        const { csv, skippedAttachments } = entriesToCsv(all);
        if (skippedAttachments > 0 &&
            !confirm(`Export will include ${all.length} entries. ${skippedAttachments} attachment file(s) cannot be represented in CSV and will not be exported. Continue?`)) return;
        downloadBytes(new TextEncoder().encode(csv), vaultFilename('.csv'), 'text/csv;charset=utf-8');
      } },
      { separator: true },
      { label: 'Open recovery tool', onClick: () => window.open('recover.html', '_blank', 'noopener') },
      { label: 'Lock', onClick: () => {
        try { history.replaceState({}, '', location.pathname); } catch { /* ignore */ }
        vault.lock(); onLocked();
      } },
    ];
  }

  function renderTopbarMobile() {
    if (state.mobile.view === 'detail') {
      const sel = state.entries.find((e) => e.id === state.selectedId);
      renderTopbar(topbar, {
        mode: 'detail',
        title: sel?.title ?? '',
        syncStatus: state.syncStatus,
        starred: !!sel?.starred,
        onToggleStar: async () => {
          if (!sel) return;
          await vault.upsert({ ...sel, starred: !sel.starred });
          await refresh();
        },
        onBack: () => {
          state.selectedId = null;
          setView('list');
          renderList();
          renderDetailPane();
          try { history.back(); } catch { /* ignore */ }
        },
        onEdit: () => sel && openForm(sel),
        onMenu: (trigger) => openMenu(trigger, buildMenuItems()),
      });
    } else {
      renderTopbar(topbar, {
        mode: 'list',
        search: state.search,
        syncStatus: state.syncStatus,
        onSearchChange: (v) => { state.search = v; renderList(); },
        onNew: () => openForm(null),
        onMenu: (trigger) => openMenu(trigger, buildMenuItems()),
      });
    }
  }

  function renderTabbarMobile() {
    renderTabbar(tabbar, {
      activeFilter: state.filter,
      counts: countsByType(state.entries),
      onPick: (key) => {
        state.filter = key;
        state.selectedId = null;
        if (state.mobile.view === 'detail') {
          setView('list');
          try { history.back(); } catch { /* ignore */ }
        }
        render();
      },
    });
  }

  async function triggerSync() {
    if (!sync) return;
    try {
      const result = await sync.sync();
      if (result.action === 'first-sync-required') showFirstSyncDialog(result.remote);
      else await refresh();
    } catch (err) {
      if (err instanceof SyncConflictError) {
        showConflictDialog(err.local, err.remote);
      } else {
        state.syncStatus = 'error';
        render();
        console.warn('sync failed', err);
      }
    }
  }

  function showFirstSyncDialog(remote) {
    openFirstSyncDialog({
      remote,
      onPull: async (password) => {
        await sync.joinFromRemote(password);
        await refresh();
      },
      onPushOverwrite: async () => {
        await sync.push();
        await refresh();
      },
      onSkip: () => { state.syncStatus = 'idle'; render(); },
    });
  }

  function showConflictDialog(local, remote) {
    openConflictDialog({
      local, remote,
      onUseLocal: async () => { await sync.resolveUseLocal(); await refresh(); },
      onUseRemote: async () => { await sync.resolveUseRemote(); await refresh(); },
      onDownloadRemote: async () => {
        const bytes = await sync.readRemoteBlob();
        downloadBytes(bytes, vaultFilename('-cloud.enc'), 'application/octet-stream');
      },
    });
  }

  function openForm(existing) {
    const priorUrl = existing?.url ?? null;
    openEntryForm({
      entry: existing,
      autoLock,
      onSave: async (entry, ops = {}) => {
        const saved = await vault.upsert(entry);
        await applyAttachmentOps(vault, saved.id, ops);
        state.selectedId = saved.id;
        await refresh();
        setView('detail');
        pushDetailHistory(saved.id);
        renderTopbarMobile();
        maybeFetchFavicon(saved, priorUrl);
      },
    });
  }

  async function maybeFetchFavicon(saved, priorUrl) {
    if (saved.type !== 'login') return;
    if (!saved.url) return;
    const urlChanged = saved.url !== priorUrl;
    if (saved.iconDataUrl && !urlChanged) return;

    const dataUrl = await fetchFavicon(saved.url);
    if (!dataUrl) return;

    const fresh = await vault.get(saved.id);
    if (!fresh) return;
    await vault.upsert({ ...fresh, iconDataUrl: dataUrl });
    await refresh();
    renderDetailPane();
  }

  // Move selection up/down within the currently visible (filtered + sorted)
  // list, in place — no per-keystroke history push, no forced view switch.
  function selectByOffset(delta) {
    const visible = filterEntries(state.entries, state.search, state.filter);
    const cur = visible.findIndex((x) => x.id === state.selectedId);
    const idx = nextIndex(cur, visible.length, delta);
    if (idx < 0) return;
    state.selectedId = visible[idx].id;
    renderList();
    renderDetailPane();
    list.querySelector('.entry-row.active')?.scrollIntoView({ block: 'nearest' });
  }

  // Any open overlay (sheet/modal, mobile menu sheet, desktop popup menu)
  // owns the keyboard — don't hijack arrow keys underneath it.
  function overlayOpen() {
    return !!document.querySelector('.sheet-backdrop, .menu-popup, .modal-backdrop');
  }

  // Keyboard shortcuts
  const keyHandler = (e) => {
    const mod = e.metaKey || e.ctrlKey;
    // With a sheet open, ⌘N/⌘E would stack a second form over the first and
    // ⌘F would pull focus to the search box behind the modal. ⌘L is
    // deliberately still live — locking must always be reachable.
    if (mod && e.key.toLowerCase() !== 'l' && overlayOpen()) return;
    if (mod && e.key.toLowerCase() === 'f') { e.preventDefault(); listApi.focusSearch(); }
    else if (mod && e.key.toLowerCase() === 'n') { e.preventDefault(); openForm(null); }
    else if (mod && e.key.toLowerCase() === 'l') {
      e.preventDefault();
      try { history.replaceState({}, '', location.pathname); } catch { /* ignore */ }
      vault.lock(); onLocked();
    }
    else if (mod && e.key.toLowerCase() === 'e') {
      const sel = state.entries.find((x) => x.id === state.selectedId);
      if (sel) { e.preventDefault(); openForm(sel); }
    }
    else if (!mod && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      const ae = document.activeElement;
      if (ae?.tagName === 'TEXTAREA' || ae?.isContentEditable || overlayOpen()) return;
      e.preventDefault();
      selectByOffset(e.key === 'ArrowDown' ? 1 : -1);
    }
  };
  document.addEventListener('keydown', keyHandler);

  if (sync) {
    sync.setStatusHandler((s) => { state.syncStatus = s; renderSidebar(); renderTopbarMobile(); });
    sync.start();
    triggerSync();
  }

  refresh();

  return {
    destroy() {
      teardownDetail();
      document.removeEventListener('keydown', keyHandler);
      window.removeEventListener('popstate', onPopState);
      if (sync) sync.stop();
    },
  };
}

function downloadBytes(bytes, filename, mime = 'application/octet-stream') {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function vaultFilename(ext) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `vault-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}${ext}`;
}
